"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLocale = exports.getLocaleFromRequest = void 0;
const constants_1 = require("./constants");
const cookies_1 = require("./cookies");
const headers_1 = require("./headers");
const tld_1 = require("./tld");
const url_1 = require("./url");
const utils_1 = require("./utils");
const webapp_constants_1 = require("@eventbrite/webapp-constants");
/**
 * Determines the locale based on a series of prioritized checks.
 *
 * @param {() => string | undefined} checks - A list of functions that return a locale or undefined.
 * @returns {string} - The first valid locale found, or the default locale.
 */
const determineLocale = (checks) => {
    for (const check of checks) {
        const locale = check();
        if (locale) {
            return locale;
        }
    }
    return constants_1.DEFAULT_LOCALE;
};
/**
 * Finds the first non-null locale preference from a series of prioritized checks.
 *
 * @param {() => string | null} checks - A list of functions that return a locale or null.
 * @returns {string | null} - The first valid locale found, or null if none found.
 */
const findLocalePreference = (checks) => {
    for (const check of checks) {
        const locale = check();
        if (locale) {
            return locale;
        }
    }
    return null;
};
/**
 * Extracts the locale from an HTTP request using various sources of information.
 * The locale preference (from cookie, URL, or headers) is resolved to its parent locale
 * and validated against the TLD's supported locales. If valid, the parent locale is returned;
 * otherwise, the TLD's default locale is returned.
 *
 * @param {IncomingMessage} request - The incoming HTTP request.
 * @returns {string} - The determined locale.
 */
const getLocaleFromRequest = (request) => {
    // Extract TLD from request
    const tld = (0, tld_1.extractTLD)(request);
    const tldWithDot = tld ? `.${tld}` : null;
    // Determine user preference in priority order
    const userPreference = findLocalePreference([
        () => (0, cookies_1.getLocaleFromCookie)(request.headers.cookie || ""),
        () => {
            const url = new URL(request.url, `https://${request.headers.host}`);
            return (0, url_1.getLocaleFromUrl)(url);
        },
        () => (0, headers_1.getLocaleFromHeaders)(request.headers),
    ]);
    // Resolve user preference to parent locale and validate against TLD's supported locales
    if (tldWithDot && userPreference) {
        const parentLocale = (0, utils_1.getLocale)(userPreference);
        if (constants_1.TLD_SUPPORTED_LOCALES[tldWithDot]?.includes(parentLocale)) {
            return parentLocale;
        }
    }
    // Return TLD's default locale or fallback to DEFAULT_LOCALE
    return tldWithDot && constants_1.TLD_DEFAULT_LOCALE[tldWithDot] ? constants_1.TLD_DEFAULT_LOCALE[tldWithDot] : constants_1.DEFAULT_LOCALE;
};
exports.getLocaleFromRequest = getLocaleFromRequest;
/**
 * Extracts the locale from a request object containing headers and optional search parameters.
 * The locale preference (from cookie, search params, or accept-language) is resolved to its parent locale
 * and validated against the TLD's supported locales. If valid, the parent locale is returned;
 * otherwise, the TLD's default locale is returned.
 *
 * @typedef {Object} GetLocaleRequest
 * @property {Headers} headers - The headers of the request.
 * @property {URLSearchParams} [searchParams] - Optional URLSearchParams query parameters.
 *
 * @param {GetLocaleRequest} request - The request object.
 * @returns {string} - The determined locale.
 */
const getLocale = ({ headers, searchParams }) => {
    // Extract TLD from host header
    const hostHeader = headers.get(webapp_constants_1.EventbriteHeader.EB_HOST);
    const tld = (0, tld_1.extractHostTLD)(hostHeader);
    const tldWithDot = tld ? `.${tld}` : null;
    // Determine user preference in priority order
    const userPreference = findLocalePreference([
        () => (0, cookies_1.getLocaleFromCookie)(headers.get("cookie") || ""),
        () => (searchParams ? (0, url_1.getLocaleFromSearchParams)(searchParams) : null),
        () => (0, headers_1.getLocaleFromAcceptLanguageHeader)(headers.get(headers_1.ACCEPT_LANGUAGE_HEADER) || ""),
    ]);
    // Resolve user preference to parent locale and validate against TLD's supported locales
    if (tldWithDot && userPreference) {
        const parentLocale = (0, utils_1.getLocale)(userPreference);
        if (constants_1.TLD_SUPPORTED_LOCALES[tldWithDot]?.includes(parentLocale)) {
            return parentLocale;
        }
    }
    // Return TLD's default locale or fallback to DEFAULT_LOCALE
    return tldWithDot && constants_1.TLD_DEFAULT_LOCALE[tldWithDot] ? constants_1.TLD_DEFAULT_LOCALE[tldWithDot] : constants_1.DEFAULT_LOCALE;
};
exports.getLocale = getLocale;
//# sourceMappingURL=request.js.map